import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import {
	AUTH_MESSAGE_CODES,
	AUTH_MESSAGE_CONTEXT,
	AUTH_TRANSACTION_CODES,
	CANCEL_HINT_CODES,
	CoreState,
	FAIL_HINT_CODES,
	MessageService,
	NOOP_HINT_CODES,
	PERSONAL_NUMBER_CODES,
	SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT,
	SmartIdAuthTransactionSelector,
	VerificationStatus,
} from "@obg/ngx.core";
import { ignoreNil } from "@obg/rxjs";
import { Observable } from "rxjs";
import { distinctUntilChanged, filter, map } from "rxjs/operators";

import { EXCLUDED_GENERIC_ERROR_MESSAGE_CODES } from "../generic/generic-login.const";
import { LoginUiFlowService } from "../login-ui/login-ui-flow.service";

@Injectable({ providedIn: "root" })
export class SmartIdLoginService {
	constructor(
		private messageService: MessageService,
		private loginUiFlowService: LoginUiFlowService,
		private store: Store<CoreState>
	) {}

	personalNumberErrors$(): Observable<string> {
		return this.messageService
			.consume$({
				messageCode: Object.values(PERSONAL_NUMBER_CODES),
			})
			.pipe(map(([error]) => error.messageCode));
	}

	loginTransactionsVerificationStatus$(): Observable<VerificationStatus> {
		return this.store
			.select(SmartIdAuthTransactionSelector.getLastStatus)
			.pipe(ignoreNil(), distinctUntilChanged());
	}

	initializeVerificationFail$(): Observable<void> {
		return this.messageService
			.consume$({
				context: [
					SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeAuthTransaction,
					SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT.initializeVerification,
				],
				messageCode: AUTH_TRANSACTION_CODES.authenticationError,
			})
			.pipe(map(() => undefined));
	}

	genericLoginError$(): Observable<string> {
		return this.loginUiFlowService.genericLoginError$().pipe(
			filter(([error]) => !EXCLUDED_GENERIC_ERROR_MESSAGE_CODES.includes(error.messageCode)),
			map(([error]) => (navigator.onLine ? error.messageCode : AUTH_MESSAGE_CODES.login.error))
		);
	}

	invalidCredentialsError$(): Observable<void> {
		return this.messageService
			.consume$({
				context: AUTH_MESSAGE_CONTEXT.loginWithToken,
				messageCode: [
					AUTH_MESSAGE_CODES.login.invalidCredentials,
					AUTH_MESSAGE_CODES.login.credentialsNotFound,
				],
			})
			.pipe(map(() => undefined));
	}

	customerVerificationFail$(): Observable<void> {
		return this.messageService
			.consume$({
				context: SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT.getVerificationStatus,
				excludeCodes: [
					...NOOP_HINT_CODES,
					...CANCEL_HINT_CODES,
					AUTH_TRANSACTION_CODES.verificationStatus.failed.alreadyUsed,
				],
			})
			.pipe(map(() => undefined));
	}

	customerVerificationCancel$(): Observable<void> {
		return this.messageService
			.consume$({
				context: SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT.getVerificationStatus,
				excludeCodes: [...NOOP_HINT_CODES, ...FAIL_HINT_CODES],
			})
			.pipe(map(() => undefined));
	}

	cancelLastVerification$(): Observable<void> {
		return this.messageService
			.consume$({
				context: SMART_ID_AUTH_TRANSACTION_MESSAGE_CONTEXT.cancelLastVerification,
			})
			.pipe(map(() => undefined));
	}
}
